import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { api, Account, startGoogleLogin } from './api';

export type RegisterPayload = {
  email: string;
  password: string;
  full_name?: string;
  phone_primary?: string;
  event: {
    name: string;
    event_date: string;
    event_time?: string;
    venue_name?: string;
    venue_address?: string;
    venue_lat?: number;
    venue_lng?: number;
    total_seats?: number;
    max_plus_ones?: number;
    invitation_template?: string;
    invitation_text?: string;
  };
};

interface AuthCtx {
  account: Account | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  loginWithGoogle: () => void;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<AuthCtx>(null as unknown as AuthCtx);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('wr_token');
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get('/auth/me')
      .then((r) => setAccount(r.data.account))
      .catch(() => {
        if (localStorage.getItem('wr_token') === token) {
          localStorage.removeItem('wr_token');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const r = await api.post('/auth/login', {
      email: email.trim().toLowerCase(),
      password: password.trim(),
    });
    localStorage.setItem('wr_token', r.data.token);
    setAccount(r.data.account);
  }, []);

  const loginWithToken = useCallback(async (token: string) => {
    localStorage.setItem('wr_token', token);
    const r = await api.get('/auth/me');
    setAccount(r.data.account);
  }, []);

  const loginWithGoogle = useCallback(() => {
    startGoogleLogin();
  }, []);

  const register = useCallback(async (payload: RegisterPayload) => {
    const r = await api.post('/auth/register', payload);
    localStorage.setItem('wr_token', r.data.token);
    setAccount(r.data.account);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('wr_token');
    setAccount(null);
    window.location.href = '/login';
  }, []);

  const value = useMemo(
    () => ({ account, loading, login, loginWithToken, loginWithGoogle, register, logout }),
    [account, loading, login, loginWithToken, loginWithGoogle, register, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  return useContext(Ctx);
}
